import { server } from "@/api";

/**
 * Get all notifications
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number for pagination
 * @param {number} params.per_page - Number of items per page
 * @param {boolean} params.unread - Filter for unread notifications only
 * @returns {Promise} - Promise with the response
 */
export const getAll = async (params = {}) => {
    try {
        console.log('API: Fetching all notifications...', params);
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.unread !== undefined) queryParams.append('unread', params.unread);
        
        const queryString = queryParams.toString();
        const url = queryString ? `api/notifications?${queryString}` : 'api/notifications';
        
        const response = await server().get(url);
        
        // Handle the new API response structure with meta
        if (response?.data) {
            if (response.data.data && Array.isArray(response.data.data)) {
                console.log(`API: Found ${response.data.data.length} notifications (page ${response.data.meta?.current_page || 1})`);
            } else {
                console.warn('API: Response has data but notifications array is missing or not an array', response.data);
            }
        } else {
            console.warn('API: Unexpected response structure', response);
        }
        
        return response;
    } catch (error) {
        console.error('API: Error fetching notifications:', error);
        // Return a valid response structure even on error
        return { 
            data: { 
                data: [],
                meta: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 10,
                    total: 0
                }
            } 
        };
    }
}

/**
 * Get notifications with pagination (alias for getAll with clearer naming)
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number for pagination
 * @param {number} params.per_page - Number of items per page
 * @param {boolean} params.unread - Filter for unread notifications only
 * @returns {Promise} - Promise with the response
 */
export const getNotificationsPaginated = async (params = {}) => {
    return getAll(params);
}

/**
 * Get unread notifications count
 * 
 * @returns {Promise} - Promise with the response
 */
export const getUnreadCount = async () => {
    try {
        console.log('API: Fetching unread count...');
        const response = await server().get('api/notifications/unread-count');
        
        if (response?.data?.count !== undefined) {
            console.log(`API: Unread count is ${response.data.count}`);
        } else {
            console.warn('API: Unexpected unread count response structure', response);
        }
        
        return response;
    } catch (error) {
        console.error('API: Error fetching unread count:', error);
        // Return a valid response structure even on error
        return { data: { count: 0 } };
    }
}

/**
 * Mark a notification as read
 * 
 * @param {number|string} id - Notification ID
 * @returns {Promise} - Promise with the response
 */
export const markAsRead = async (id) => {
    try {
        return await server().post(`api/notifications/${id}/mark-read`);
    } catch (error) {
        console.error(`API: Error marking notification ${id} as read:`, error);
        throw error;
    }
}

/**
 * Mark a notification as unread
 * 
 * @param {number|string} id - Notification ID
 * @returns {Promise} - Promise with the response
 */
export const markAsUnread = async (id) => {
    try {
        return await server().post(`api/notifications/${id}/mark-unread`);
    } catch (error) {
        console.error(`API: Error marking notification ${id} as unread:`, error);
        throw error;
    }
}

/**
 * Mark all notifications as read
 * 
 * @returns {Promise} - Promise with the response
 */
export const markAllAsRead = async () => {
    try {
        return await server().post('api/notifications/mark-all-read');
    } catch (error) {
        console.error('API: Error marking all notifications as read:', error);
        throw error;
    }
}

/**
 * Delete a notification
 * 
 * @param {number|string} id - Notification ID
 * @returns {Promise} - Promise with the response
 */
export const deleteNotification = async (id) => {
    try {
        return await server().delete(`api/notifications/${id}`);
    } catch (error) {
        console.error(`API: Error deleting notification ${id}:`, error);
        throw error;
    }
}

export default {
    getAll,
    getNotificationsPaginated,
    getUnreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification
} 